/**
 * Permission Manager Explorer Configuration
 *
 * Admin-only explorer for managing permission -> operation mappings.
 * Uses ConfigurableBaseExplorer pattern with quickFilters for filtering
 * and custom toolbar for granting permissions.
 *
 * Filters are managed by CBE's built-in filter system via quickFilters[].
 * The service adapter reads filter values from QueryParams.filters[].
 * Client-side filtering (~105 rows, small dataset).
 */
import {
  BaseExplorerConfig,
  EntityServiceAdapter,
  QueryParams,
  QueryResult,
  ExplorerFeatureConfig,
  PresentationConfig,
} from '../../../types/baseExplorerConfig';
import { fetchMappings } from '../../../services/permissionAdminService';
import { paginateClientSide } from '../../../utils/pagination';
import type { PermissionMapping } from '../../../services/permissionAdminService';
import { faShieldHalved } from '@fortawesome/free-solid-svg-icons';

// ─── Service Adapter ──────────────────────────────────────────────────────────

const serviceAdapter: EntityServiceAdapter<any> = {
  async fetchData(params: QueryParams): Promise<QueryResult<any>> {
    // Fetch all mappings (small dataset ~105 rows, client-side filtering)
    let mappings = await fetchMappings();

    // Apply client-side filters from QueryParams.filters[]
    if (params?.filters) {
      for (const f of params.filters) {
        const val = (f.value as string || '').toLowerCase();
        if (!val) continue;
        switch (f.field) {
          case 'permission':
            mappings = mappings.filter(m => m.permission.toLowerCase().includes(val));
            break;
          case 'feature':
            mappings = mappings.filter(m => m.feature.toLowerCase().includes(val));
            break;
          case 'operation':
            mappings = mappings.filter(m => m.operation.toLowerCase().includes(val));
            break;
          case 'granted_by':
            mappings = mappings.filter(m => ((m as any).granted_by || '').toLowerCase().includes(val));
            break;
          case 'created_at': {
            // CBE decomposes dateRange into two FilterParams with operator gte/lte
            if (f.operator === 'gte') {
              const fromDate = new Date(f.value as string);
              mappings = mappings.filter(m => new Date((m as any).created_at) >= fromDate);
            }
            if (f.operator === 'lte') {
              const toDate = new Date(f.value as string);
              toDate.setHours(23, 59, 59, 999);
              mappings = mappings.filter(m => new Date((m as any).created_at) <= toDate);
            }
            break;
          }
        }
      }
    }

    // Apply search from base explorer search bar
    if (params?.search?.term) {
      const term = params.search.term.toLowerCase();
      mappings = mappings.filter(m =>
        m.permission.toLowerCase().includes(term) ||
        m.feature.toLowerCase().includes(term) ||
        m.operation.toLowerCase().includes(term),
      );
    }

    // Apply sorting
    if (params?.sort && params.sort.length > 0) {
      const { field, direction } = params.sort[0];
      mappings.sort((a, b) => {
        const aVal = String((a as any)[field] || '');
        const bVal = String((b as any)[field] || '');
        const cmp = aVal.localeCompare(bVal);
        return direction === 'desc' ? -cmp : cmp;
      });
    }

    // Map _id for CBE compatibility, then paginate the mapped list.
    const mapped = mappings.map((m: PermissionMapping) => ({ ...m, _id: m.id }));
    return paginateClientSide(mapped, params?.page, params?.limit ?? 50);
  },
};

// ─── Field Transformers ──────────────────────────────────────────────────────

const timestampTransformer = {
  transform: (value: any) => {
    if (!value) return '';
    const d = new Date(value);
    return d.toLocaleString('en-CA', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
      hour12: false,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  },
};

const nullDashTransformer = {
  transform: (value: any) => value ?? '-',
};

const PERMISSION_FIELD_TRANSFORMERS: Record<string, any> = {
  created_at: timestampTransformer,
  granted_by: nullDashTransformer,
};

// ─── Features ─────────────────────────────────────────────────────────────────

const PERMISSION_FEATURES: ExplorerFeatureConfig = {
  pagination: {
    enabled: true,
    defaultPageSize: 50,
    pageSizeOptions: [25, 50, 100],
    showPageInfo: true,
  },
  sorting: {
    enabled: true,
    multiColumn: false,
    defaultSort: [{ field: 'permission', direction: 'asc' }],
  },
  filtering: {
    enabled: true,
    quickFilters: [
      {
        field: 'permission',
        label: 'Permission',
        type: 'textSearch',
      },
      {
        field: 'feature',
        label: 'Feature',
        type: 'textSearch',
      },
      { field: 'operation', label: 'Operation', type: 'textSearch' },
      { field: 'granted_by', label: 'Granted By', type: 'textSearch' },
      { field: 'created_at', label: 'Created', type: 'dateRange' },
    ],
    advancedFilters: false,
  },
  searching: {
    enabled: true,
    placeholder: 'Search permissions, features, operations...',
    searchFields: ['permission', 'feature', 'operation'],
    debounceMs: 300,
  },
  editing: {
    enabled: true,
    mode: 'modal',
    allowCreate: false,
    allowUpdate: false,
    allowDelete: true,
  },
};

// ─── Presentation ─────────────────────────────────────────────────────────────

const PERMISSION_PRESENTATION: PresentationConfig = {
  title: 'Permission Manager',
  subtitle: 'Manage permission -> operation mappings',
  icon: faShieldHalved,

  columnConfig: [
    {
      field: 'permission',
      title: 'Permission',
      width: 180,
      minWidth: 120,
      resizable: true,
      sortable: true,
      filterable: true,
      hidden: false,
      cellRenderer: {
        type: 'custom' as const,
        options: {
          render: (value: any) =>
            `<span style="font-weight:600;color:var(--em3-color-text-primary)">${value}</span>`,
        },
      },
    },
    {
      field: 'feature',
      title: 'Feature',
      width: 150,
      minWidth: 100,
      resizable: true,
      sortable: true,
      filterable: true,
      hidden: false,
    },
    {
      field: 'operation',
      title: 'Operation',
      width: 140,
      minWidth: 100,
      resizable: true,
      sortable: true,
      filterable: true,
      hidden: false,
    },
    {
      field: 'granted_by',
      title: 'Granted By',
      width: 130,
      minWidth: 100,
      resizable: true,
      sortable: true,
      filterable: true,
      hidden: false,
      cellRenderer: {
        type: 'custom' as const,
        options: {
          render: (value: any) => value || '<span style="color:var(--em3-color-text-secondary)">-</span>',
        },
      },
    },
    {
      field: 'created_at',
      title: 'Created',
      width: 150,
      minWidth: 120,
      resizable: true,
      sortable: true,
      filterable: true,
      hidden: false,
      cellRenderer: {
        type: 'custom' as const,
        options: {
          render: (value: any) => {
            if (!value) return '';
            const d = new Date(value);
            return d.toLocaleString('en-CA', {
              year: 'numeric', month: '2-digit', day: '2-digit',
              hour: '2-digit', minute: '2-digit',
              hour12: false,
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
          },
        },
      },
    },
  ],
  layout: {
    minHeight: 400,
    responsive: true,
    showToolbar: true,
    showFooter: true,
  },
};

// ─── Exported config ──────────────────────────────────────────────────────────

const permissionManagerConfig: BaseExplorerConfig<any> = {
  entityName: 'Permission Mapping',
  complexityLevel: 'moderate',
  displayFields: ['permission', 'feature', 'operation', 'granted_by', 'created_at'],
  fieldTransformers: PERMISSION_FIELD_TRANSFORMERS as any,
  serviceAdapter: serviceAdapter as any,
  features: PERMISSION_FEATURES,
  presentation: PERMISSION_PRESENTATION,
};

export default permissionManagerConfig;
